import { useMemo, useState } from 'react';
import { useModelStore } from '../../../stores/modelStore';
import { canManageModel } from './modelPermissions';
import { BsBookmark, BsThreeDotsVertical } from 'react-icons/bs';
import { HospitalBuildingPinIcon, LightBulbShineIcon } from './icons';
import { FaChevronRight } from 'react-icons/fa';
import {
  DrawerBody,
  DrawerContent,
  IconButton as ChakraIconButton,
  MenuButton as ChakraMenuButton,
} from '@chakra-ui/react';
import { Drawer } from '../../../components/UI-CHAKRA/Drawer';
import { DrawerOverlay } from '../../../components/UI-CHAKRA/DrawerOverlay';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { IconButton } from '../../../components/UI-CHAKRA/IconButton';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { Menu } from '../../../components/UI-CHAKRA/Menu/Menu';
import { MenuList } from '../../../components/UI-CHAKRA/Menu/MenuList';
import { MenuItem } from '../../../components/UI-CHAKRA/Menu/MenuItem';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useLoading } from '../../../helpers/useLoading';
import { Model } from '../services';

type ModelCategory = 'sugestao' | 'institucional' | 'meusModelos';

const CATEGORY_STYLES: Record<
  ModelCategory,
  { label: string; bg: string; color: string; icon: JSX.Element }
> = {
  sugestao: {
    label: 'Sugestão',
    bg: '#FFF8F9',
    color: '#664961',
    icon: <LightBulbShineIcon size={12} />,
  },
  institucional: {
    label: 'Institucional',
    bg: '#F4FFF7',
    color: '#0E6928',
    icon: <HospitalBuildingPinIcon size={12} />,
  },
  meusModelos: {
    label: 'Meus modelos',
    bg: '#EEF1FF',
    color: '#445AB5',
    icon: <BsBookmark size={12} />,
  },
};

function getModelCategory(model: Model): ModelCategory {
  if (model.sponsored) return 'sugestao';
  if (model.origin === 'institutional') return 'institucional';
  return 'meusModelos';
}

function pluralizeItens(count?: number): string {
  if (count === undefined) return '';
  return count === 1 ? '1 item' : `${count} itens`;
}

interface PrescriptionModelCardProps {
  model: Model;
  itemCount?: number;
  onSelectModel: (model: Model) => Promise<void>;
  onEditModel?: (model: Model) => void;
  onDeleteModel?: (model: Model) => Promise<void>;
}

export function PrescriptionModelCard(props: PrescriptionModelCardProps) {
  const { model, itemCount, onSelectModel, onEditModel, onDeleteModel } = props;
  const [loadingSelect, runWithLoadingSelect] = useLoading();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const isMobile = useIsMobile();

  const isInstitutionalModelAdmin = useModelStore((store) => store.isInstitutionalModelAdmin);

  const category = useMemo(() => getModelCategory(model), [model]);
  const categoryStyle = CATEGORY_STYLES[category];

  const showActionsMenu =
    canManageModel(model, isInstitutionalModelAdmin) && (Boolean(onEditModel) || Boolean(onDeleteModel));

  const handleSelect = () => {
    runWithLoadingSelect(async () => {
      await onSelectModel(model);
    });
  };

  const handleEdit = () => {
    setActionSheetOpen(false);
    onEditModel?.(model);
  };

  const handleDelete = () => {
    setActionSheetOpen(false);
    onDeleteModel?.(model);
  };

  const renderActionsTrigger = () => {
    const triggerIcon = <BsThreeDotsVertical size={16} color={'#1E252B'} />;

    if (isMobile) {
      return (
        <Box onClick={(e) => e.stopPropagation()} data-testid={'model-actions-trigger'}>
          <IconButton
            aria-label={'Ações do modelo'}
            icon={triggerIcon}
            onClick={() => setActionSheetOpen(true)}
            variant={'ghost'}
            size={'xs'}
            w={'24px'}
            h={'24px'}
            minW={'24px'}
            borderRadius={'4px'}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
          />
        </Box>
      );
    }

    return (
      <Box onClick={(e) => e.stopPropagation()} data-testid={'model-actions-trigger'}>
        <Menu placement={'bottom-end'} autoSelect={false} isLazy>
          <ChakraMenuButton
            as={ChakraIconButton}
            aria-label={'Ações do modelo'}
            icon={triggerIcon}
            variant={'ghost'}
            size={'xs'}
            w={'24px'}
            h={'24px'}
            minW={'24px'}
            borderRadius={'4px'}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
          />
          <MenuList minW={'120px'} py={'4px'}>
            <MenuItem
              fontSize={'14px'}
              onClick={() => onEditModel?.(model)}
              data-testid={'model-action-edit'}
            >
              Editar
            </MenuItem>
            <MenuItem
              fontSize={'14px'}
              color={'error.main'}
              onClick={() => onDeleteModel?.(model)}
              data-testid={'model-action-delete'}
            >
              Remover
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    );
  };

  return (
    <>
      <Flex
        data-testid={'prescription-model-card'}
        data-cy={model.name}
        direction={'column'}
        gap={'8px'}
        p={'8px'}
        h={'84px'}
        w={'100%'}
        bg={'white'}
        border={'1px solid #DEE2E6'}
        borderRadius={'8px'}
        cursor={'pointer'}
        transition={'background 0.15s ease, box-shadow 0.15s ease'}
        _hover={{
          bg: 'gray.lightest',
          boxShadow: '0px 6px 4px rgba(170, 170, 170, 0.2)',
        }}
        onClick={handleSelect}
      >
        <Flex w={'100%'} gap={'8px'} alignItems={'center'}>
          <Flex flex={'1 1 auto'} minW={0} gap={'8px'} alignItems={'center'}>
            <Flex
              data-testid={`model-badge-${category}`}
              alignItems={'center'}
              justifyContent={'center'}
              gap={'5px'}
              h={'24px'}
              px={'8px'}
              py={'4px'}
              bg={categoryStyle.bg}
              color={categoryStyle.color}
              borderRadius={'15px'}
              flexShrink={0}
            >
              {categoryStyle.icon}
              <Text fontSize={'12px'} fontWeight={500} lineHeight={'1.3'} whiteSpace={'nowrap'}>
                {categoryStyle.label}
              </Text>
            </Flex>

            {itemCount !== undefined && (
              <Text
                fontSize={'12px'}
                fontWeight={500}
                color={'#868E96'}
                lineHeight={'1.3'}
                whiteSpace={'nowrap'}
              >
                {pluralizeItens(itemCount)}
              </Text>
            )}
          </Flex>

          {showActionsMenu && renderActionsTrigger()}

          <Flex
            alignItems={'center'}
            justifyContent={'center'}
            w={'24px'}
            h={'24px'}
            flexShrink={0}
            color={'#1E252B'}
          >
            {loadingSelect ? <Spinner thickness={'2px'} w={'14px'} h={'14px'} /> : <FaChevronRight size={12} />}
          </Flex>
        </Flex>

        <Text
          fontSize={'14px'}
          fontWeight={500}
          color={'#1E252B'}
          lineHeight={'1.3'}
          w={'100%'}
          h={'36px'}
          overflow={'hidden'}
          textOverflow={'ellipsis'}
          whiteSpace={'nowrap'}
        >
          {model.name}
        </Text>
      </Flex>

      {isMobile && showActionsMenu && (
        <ModelActionSheet
          isOpen={actionSheetOpen}
          modelName={model.name}
          showEdit={Boolean(onEditModel)}
          showDelete={Boolean(onDeleteModel)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClose={() => setActionSheetOpen(false)}
        />
      )}
    </>
  );
}

interface ModelActionSheetProps {
  isOpen: boolean;
  modelName: string;
  showEdit: boolean;
  showDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function ModelActionSheet(props: ModelActionSheetProps) {
  const { isOpen, modelName, showEdit, showDelete, onEdit, onDelete, onClose } = props;
  return (
    <Drawer isOpen={isOpen} placement={'bottom'} onClose={onClose} autoFocus={false}>
      <DrawerOverlay bg={'rgba(0, 0, 0, 0.4)'} />
      <DrawerContent borderTopRadius={'16px'} pb={'env(safe-area-inset-bottom, 0px)'}>
        <DrawerBody px={'16px'} pt={'12px'} pb={'24px'}>
          <Box
            w={'40px'}
            h={'4px'}
            bg={'#CBBFE6'}
            borderRadius={'2px'}
            mx={'auto'}
            mb={'16px'}
            data-testid={'model-action-sheet-handle'}
          />
          <Text
            fontSize={'16px'}
            fontWeight={600}
            color={'#1E252B'}
            textAlign={'center'}
            mb={'20px'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            {modelName}
          </Text>
          <Flex gap={'12px'}>
            {showEdit && (
              <Button
                flex={1}
                h={'48px'}
                variant={'outline'}
                borderColor={'primary.main'}
                color={'primary.main'}
                borderRadius={'40px'}
                fontSize={'14px'}
                fontWeight={600}
                onClick={onEdit}
                data-testid={'model-action-sheet-edit'}
              >
                Editar
              </Button>
            )}
            {showDelete && (
              <Button
                flex={1}
                h={'48px'}
                variant={'outline'}
                borderColor={'#FF6161'}
                color={'#FF6161'}
                borderRadius={'40px'}
                fontSize={'14px'}
                fontWeight={600}
                onClick={onDelete}
                data-testid={'model-action-sheet-delete'}
              >
                Remover
              </Button>
            )}
          </Flex>
          <Flex justifyContent={'center'} mt={'16px'}>
            <Box
              as={'button'}
              onClick={onClose}
              bg={'transparent'}
              border={'none'}
              cursor={'pointer'}
              p={'8px 12px'}
              data-testid={'model-action-sheet-close'}
            >
              <Text fontSize={'14px'} fontWeight={500} color={'primary.main'} textDecoration={'underline'}>
                Fechar
              </Text>
            </Box>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
